/**
 * BoxDetail — single SDB box record.
 *
 * Port of legacy SPFx `SdbManagementScreen.handleLookup` detail rendering
 * (src/webparts/accountOpeningV2/components/screens/SdbManagementScreen.tsx:319).
 *
 * Fixture-free: every value comes from `useSdbDetail` → `SdbInqService`
 * (dual AcctInq extension calls: `x_SafeDepBoxInfoRec` + `x_SafeDepAcctInfo`).
 * Any field Core Director omits renders "—".
 */
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ArrowLeft, Archive, KeyRound, RefreshCw, UserX } from 'lucide-react';
import {
  Button,
  Pill,
  WidgetCard,
  KVPair,
  StageRibbon,
  Banner,
} from '@/components/primitives';
import { formatLocalDateTime } from '@/utils/time';
import { DeputyList } from './components/DeputyList';
import { useSdbDetail } from './hooks/useSdbDetail';
import type { BoxDetail as BoxDetailType, SdbDeputyRow } from './types';
import './rsm.css';

const STAGES: ReadonlyArray<{ label: string }> = [
  { label: 'Active' },
  { label: 'Past due' },
  { label: 'Drilled' },
  { label: 'Vacant' },
];

function resolveStage(b: BoxDetailType): 0 | 1 | 2 | 3 {
  if (b.vacant) return 3;
  if ((b.lateFee ?? 0) > 0 || (b.balance ?? 0) > 0 || (b.daysOverdue ?? 0) > 0) return 1;
  return 0;
}

function currency(n: number | null): string {
  if (n == null) return '—';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function toDeputyRows(
  renters: BoxDetailType['renters'],
  primaryCif: string | null,
): ReadonlyArray<SdbDeputyRow> {
  return renters
    .filter((r) => r.cifId && r.cifId !== primaryCif)
    .map((r) => {
      // Narrow relationshipCode to JO/JT for the legacy DeputyList prop.
      const rel: 'JO' | 'JT' = r.relationshipCode === 'JT' ? 'JT' : 'JO';
      return {
        cifId: r.cifId ?? '',
        customerName: r.name ?? '—',
        relationshipCode: rel,
        addedAt: r.addedDate ?? '',
        addedBy: '',
        ...(r.phone ? { phone: r.phone } : {}),
      };
    });
}

export function BoxDetail(): JSX.Element {
  const { acctId } = useParams<{ acctId: string }>();
  const navigate = useNavigate();
  const { data: box, loading, error, reload } = useSdbDetail(acctId);
  // BACKLOG-1297 — "Record access" answered with window.alert(), which never
  // paints in the Teams webview; the message now shows in the Actions card.
  const [notice, setNotice] = useState<string | null>(null);

  const deputies = useMemo(
    () => (box ? toDeputyRows(box.renters, box.primaryCifId) : []),
    [box],
  );

  if (loading) {
    return (
      <div className="rsm-detail">
        <p>Loading box {acctId}…</p>
      </div>
    );
  }

  if (error || !box) {
    return (
      <div className="rsm-detail">
        <div className="rsm-banner rsm-banner--err" role="alert">
          {error ?? 'Box not found.'}
        </div>
        <Button variant="ghost" startIcon={<ArrowLeft size={14} />} onClick={() => navigate('..')}>
          Back
        </Button>
      </div>
    );
  }

  const stageIndex = resolveStage(box);

  return (
    <div className="rsm-detail">
      <div className="rsm-detail__crumb">
        <Button variant="ghost" startIcon={<ArrowLeft size={14} />} onClick={() => navigate('..')}>
          Dashboard
        </Button>
        <h1>Box {box.boxNumber ?? box.acctId}</h1>
        {box.vacant && <Pill variant="ok">Vacant</Pill>}
        {box.keysMissing && <Pill variant="err">Keys missing</Pill>}
        {box.restrictedEntry && <Pill variant="warn">Restricted entry</Pill>}
        {!box.vacant && ((box.lateFee ?? 0) > 0 || (box.daysOverdue ?? 0) > 0) && (
          <Pill variant="warn">Past due</Pill>
        )}
        <Button variant="ghost" startIcon={<RefreshCw size={14} />} onClick={reload}>
          Refresh
        </Button>
      </div>

      <StageRibbon stages={[...STAGES]} currentIndex={stageIndex} />

      <WidgetCard title="Box & rent" stripe="pipeline">
        <div className="rsm-detail__kvgrid">
          <KVPair label="Account ID" value={box.acctId} />
          <KVPair label="Branch" value={box.branchId ?? '—'} />
          <KVPair label="Product code" value={box.productCode ?? '—'} />
          <KVPair label="Size" value={box.sizeCode ?? '—'} />
          <KVPair label="Annual rent" value={currency(box.rentalFeeDue)} />
          <KVPair label="Rent paid through" value={box.rentPaidToDate ?? '—'} />
          <KVPair label="Next due" value={box.nextDueDate ?? '—'} />
          <KVPair
            label="Days overdue"
            value={box.daysOverdue == null ? '—' : String(box.daysOverdue)}
          />
          <KVPair label="Balance (field 133)" value={currency(box.balance)} />
          <KVPair label="Late fee (field 157)" value={currency(box.lateFee)} />
          <KVPair
            label="Key deposit (field 800)"
            value={
              box.keyDeposit == null
                ? '—'
                : box.keyDeposit > 0
                ? currency(box.keyDeposit)
                : 'None'
            }
          />
          <KVPair
            label="Last accessed"
            value={box.lastAccessDate ? formatLocalDateTime(box.lastAccessDate) : '—'}
          />
          <KVPair
            label="Lifetime access count"
            value={box.accessCount == null ? '—' : String(box.accessCount)}
          />
          <KVPair label="Status" value={box.status ?? '—'} />
        </div>
      </WidgetCard>

      <WidgetCard title="Primary customer" stripe="queue">
        <div className="rsm-detail__kvgrid">
          <KVPair label="Name" value={box.primaryCustomerName ?? '—'} />
          <KVPair label="CIF" value={box.primaryCifId ?? '—'} />
        </div>
      </WidgetCard>

      <WidgetCard title="Deputies & joint renters" stripe="staff">
        <DeputyList deputies={deputies} canAdd={!box.vacant} />
      </WidgetCard>

      <WidgetCard title="Actions" stripe="approvals">
        <div className="rsm-detail__kvgrid">
          <Button
            variant="primary"
            startIcon={<Archive size={14} />}
            onClick={() => navigate('close')}
            disabled={box.vacant}
          >
            Start close-out wizard
          </Button>
          <Button
            variant="ghost"
            startIcon={<KeyRound size={14} />}
            onClick={() =>
              setNotice('Recording an access needs the signature pad surface, which is not live yet (Wave 3).')
            }
          >
            Record access
          </Button>
          <Button
            variant="ghost"
            startIcon={<UserX size={14} />}
            onClick={() => navigate('all-deceased')}
          >
            All-Deceased wizard
          </Button>
        </div>
        {notice && (
          <Banner tone="info">
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-4)' }}>
              <span style={{ flex: 1 }}>{notice}</span>
              <Button size="sm" variant="ghost" onClick={() => setNotice(null)}>Dismiss</Button>
            </div>
          </Banner>
        )}
      </WidgetCard>
    </div>
  );
}
